'use client';

import { useEffect, useState } from 'react';

import './globals.css';

import { Geist } from 'next/font/google';
import { useParams } from 'next/navigation';

import ErrorLayout from '@/components/global/ErrorLayout';
import { Header } from '@/components/header/Header';
import { GlobalDataProvider } from '@/providers/GlobalDataProvider';
import { NextIntlProvider } from '@/providers/NextIntlProvider';
import { WagmiProvider } from '@/providers/WagmiProvider';
import { E_Locales } from '@/ts/enums/E_Locales';

const geistSans = Geist({
  variable: '--font-geist-sans',
  subsets: ['latin']
});

type T_ErrorProps = {
  error: Error;
  reset: () => void;
};

const Error = ({ error, reset: _reset }: T_ErrorProps) => {
  const params = useParams();

  const currentLocale = (params?.locale as E_Locales) || E_Locales.EN;
  const [messages, setMessages] = useState<Record<string, unknown> | null>(
    null
  );
  const [isRewardSubdomain, setIsRewardSubdomain] = useState(false);

  // Check if error is a 404/not found error
  const errorAny = error as Error & {
    digest?: string;
    code?: string;
  };
  const errorMessage = error?.message?.toLowerCase() || '';
  const isNotFoundError =
    errorMessage.includes('not found') ||
    errorMessage.includes('404') ||
    errorAny?.digest === 'NEXT_NOT_FOUND' ||
    errorAny?.code === 'NEXT_NOT_FOUND';
  useEffect(() => {
    //! Optionally log the error to an error reporting service
    console.error('Error ->', error);
  }, [error]);

  // Detect if we're on the reward subdomain
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const normalizedSubdomain = (
      process.env.NEXT_PUBLIC_SUBDOMAIN_NAME || 'reward'
    ).toLowerCase();
    const hostname = window.location.hostname.toLowerCase();

    const matchesHostname =
      normalizedSubdomain.length > 0 &&
      hostname.startsWith(`${normalizedSubdomain}.`);

    setIsRewardSubdomain(matchesHostname);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const mod = await import(`@/translations/${currentLocale}.json`);
        if (mounted) setMessages(mod.default);
      } catch (e) {
        console.error('Failed to load messages for locale', currentLocale, e);
        if (mounted) setMessages({});
      }
    })();
    return () => {
      mounted = false;
    };
  }, [currentLocale]);

  return (
    <html className={geistSans.variable}>
      <body>
        <WagmiProvider>
          <GlobalDataProvider user={null}>
            {messages ? (
              <NextIntlProvider locale={currentLocale} messages={messages}>
                <Header type={isRewardSubdomain ? 'reward' : 'default'} />
                <main className="bg-main-gradient h-main relative z-1">
                  <ErrorLayout
                    type={isNotFoundError ? 'not-found' : 'internal'}
                    className={
                      isNotFoundError
                        ? 'bg-[linear-gradient(180.05deg,#10B981_-16.42%,#000000_31.08%)] md:bg-[linear-gradient(89.93deg,#10B981_-2.19%,#000000_38.42%)]'
                        : 'bg-[linear-gradient(179deg,hsla(0,74.7%,15.5%,1)_0%,hsla(0,0%,0%,1)_100%)] md:bg-[linear-gradient(90deg,hsla(0,75%,15%,1)_0%,hsla(0,0%,0%,1)_100%)]'
                    }
                    imagePath={
                      isNotFoundError
                        ? '/assets/images/errorLayouts/404.png'
                        : '/assets/images/errorLayouts/oops.png'
                    }
                  />
                </main>
              </NextIntlProvider>
            ) : (
              <div className="min-h-screen bg-background" />
            )}
          </GlobalDataProvider>
        </WagmiProvider>
      </body>
    </html>
  );
};

export default Error;
